import { bind } from 'bind-decorator';
import { Environment } from '../vrhtml';

//-----------------------------------------------------------------------------
// Interfaces for websocket notifications. A bunch of these can die when the
// json interfaces match the internal naming scheme.
//-----------------------------------------------------------------------------

export interface BaseMessage
{
	type: string;
	returnAddress?: string;
	message_id?: number;
}

// For use with built-in 'request_handlers' => 'request_handlers_response' support.
export interface HandlersListMsg extends BaseMessage
{
	handlers: string[];
}

// Requests a message back when the specified mailbox registers with the server
interface RequestMailboxRegistrationNotification extends BaseMessage
{
	// type: request_mailbox_registration_notification
	mailbox_name: string;
}


interface MessageWait
{
	callback: ( msg: BaseMessage ) => void;
	nMessageId: number;
}

export class CVRMailbox
{
	// a websocket back to VRserver so we can get notifications about input system state changes
	private m_wsWebSocketToServer: WebSocket = undefined;

	public connected: boolean;
	public m_sMailboxName: string;
	public m_oHandlers: { [index: string]: ( msg: BaseMessage ) => void } = {};
	public m_oWaits: { [index: string]: MessageWait[] } = {};
	public m_oConnectWaits: ( () => void )[] = [];
	public m_fnConnectResolve: () => void = undefined;
	private m_sWebSecret: string;
	private m_nNextMessageNumber = 1;
	private static s_nNextMailboxNumber = 1;

	//-----------------------------------------------------------------------------
	//-----------------------------------------------------------------------------
	@bind public OpenWebSocketToHost(): Promise<void>
	{
		return new Promise( ( resolve, reject ) =>
		{
			console.log( "Connecting vrmailbox " + this.m_sMailboxName + "..." );
			let sAddr = "ws://localhost:27062";
			if ( this.m_sWebSecret )
			{
				sAddr += "?secret=" + this.m_sWebSecret;
			}

			this.m_fnConnectResolve = resolve;
			this.m_wsWebSocketToServer = new WebSocket( sAddr );
			this.m_wsWebSocketToServer.addEventListener( "open", ( event ) => { this.OnWebSocketOpen( event ); resolve(); } );
			this.m_wsWebSocketToServer.addEventListener( "message", this.OnWebSocketMessage );
			this.m_wsWebSocketToServer.addEventListener( "close", this.OnWebSocketClose );
		} );
	}

	//-----------------------------------------------------------------------------
	// Purpose: Makes sure there's a unique name for this mailbox so that replies
	//			and waiting for other mailboxes will work
	//-----------------------------------------------------------------------------
	private static EnsureUniqueName( sRawName: string ): string
	{
		if ( sRawName.includes( "/" ) )
		{
			return sRawName;
		}

		let sSuffix;
		if ( VRHTML )
		{
			sSuffix = VRHTML.GetWebHelperId();
		}
		else
		{
			// Browsers will just guess at unique names
			// based on timestamp. This will fail if two
			// browsers start in the same second.
			sSuffix = Date.now().toString();
		}

		return sRawName + "/" + sSuffix + "_" + ( this.s_nNextMailboxNumber++ );
	}

	//-----------------------------------------------------------------------------
	//-----------------------------------------------------------------------------
	public async Init( sMailboxName: string, sWebSecret?: string ): Promise<void>
	{
		this.m_sMailboxName = CVRMailbox.EnsureUniqueName( sMailboxName );
		this.m_sWebSecret = sWebSecret;
		this.connected = false;

		return this.OpenWebSocketToHost();
	}

	//-----------------------------------------------------------------------------
	//-----------------------------------------------------------------------------
	public get name(): string
	{
		return this.m_sMailboxName;
	}

	//-----------------------------------------------------------------------------
	//-----------------------------------------------------------------------------
	@bind private OnWebSocketOpen( event: any )
	{
		this.connected = true;

		this.WebSocketSend( "mailbox_open " + this.m_sMailboxName );

		window.addEventListener(
			"beforeunload",
			() =>
			{
				this.WebSocketSend( "websocket_close" );
			} );

		if ( this.m_fnConnectResolve )
		{
			this.m_fnConnectResolve();
			this.m_fnConnectResolve = undefined;
		}

		for ( let fnWaiter of this.m_oConnectWaits )
		{
			fnWaiter();
		}
		this.m_oConnectWaits = [];
	}

	//-----------------------------------------------------------------------------
	//-----------------------------------------------------------------------------
	@bind private OnWebSocketClose( event: any )
	{
		console.log( "Lost connection to host..." );

		this.connected = false;

		this.OpenWebSocketToHost();
	}


	//-----------------------------------------------------------------------------
	//-----------------------------------------------------------------------------
	@bind private WebSocketSend( sData: string )
	{
		if ( this.m_wsWebSocketToServer == undefined )
			return;

		if ( this.m_wsWebSocketToServer.readyState != 1 )
			return;

		this.m_wsWebSocketToServer.send( sData );
	}

	//-----------------------------------------------------------------------------
	//-----------------------------------------------------------------------------
	@bind private OnWebSocketMessage( event: any )
	{
		let jsonMessage = JSON.parse( event.data ) as BaseMessage;
		let bHandled = false;

		if ( this.m_oHandlers.hasOwnProperty( jsonMessage.type ) )
		{
			this.m_oHandlers[jsonMessage.type]( jsonMessage );
			bHandled = true;
		}

		if ( this.m_oWaits.hasOwnProperty( jsonMessage.type ) )
		{
			let bFoundOne = false;
			for ( let fnWaiter of this.m_oWaits[jsonMessage.type] )
			{
				if ( fnWaiter.nMessageId == jsonMessage.message_id )
				{
					fnWaiter.callback( jsonMessage );
					bFoundOne = true;
				}
			}

			if ( bFoundOne )
			{
				this.m_oWaits[jsonMessage.type] = this.m_oWaits[jsonMessage.type].filter(
					( value: MessageWait ) =>
					{
						return value.nMessageId == jsonMessage.message_id;
					} );
			}
			else
			{
				console.log( `Received a ${jsonMessage.type} message, but didn't have a `
					+ `matching message_id. Did the other end forget to mirror message_id?` );
			}

			bHandled = true;
		}

		if ( !bHandled )
		{
			console.log( "Received unhandled message: ", jsonMessage );
		}
	}

	//-----------------------------------------------------------------------------
	//-----------------------------------------------------------------------------
	public RegisterHandler( sType: string, fnHandler: ( msg: BaseMessage ) => void )
	{
		this.m_oHandlers[sType] = fnHandler;
	}

	//-----------------------------------------------------------------------------
	// Purpose: Sends a message to the specified mailbox
	//-----------------------------------------------------------------------------
	public SendMessage( sDestMailbox: string, msg: BaseMessage )
	{
		this.WebSocketSend( "mailbox_send " + sDestMailbox + " " + JSON.stringify( msg ) );
	}

	//-----------------------------------------------------------------------------
	// Purpose: Waits for a message of the specified type
	//-----------------------------------------------------------------------------
	public WaitForMessage( sMessageType: string, nMessageId: number ): Promise<BaseMessage>
	{
		let newPromise = new Promise<BaseMessage>( ( resolve, reject ) =>
		{
			if ( !this.m_oWaits[sMessageType] )
			{
				this.m_oWaits[sMessageType] = [];
			}
			this.m_oWaits[sMessageType].push(
				{
					callback: resolve,
					nMessageId,
				} );
		},
		);

		return newPromise;
	}

	//-----------------------------------------------------------------------------
	// Purpose: Waits connection
	//-----------------------------------------------------------------------------
	public WaitForConnect(): Promise<BaseMessage>
	{
		return new Promise<BaseMessage>( ( resolve, reject ) =>
		{
			if ( this.connected )
			{
				resolve();
			}
			else
			{
				this.m_oConnectWaits.push( resolve );
			}
		},
		);
	}


	//-----------------------------------------------------------------------------
	// Purpose: Waits for a mailbox to connect
	//-----------------------------------------------------------------------------
	public async WaitForMailbox( sMailboxName: string )
	{
		let msg: RequestMailboxRegistrationNotification =
			{
				type: "request_mailbox_registration_notification",
				mailbox_name: sMailboxName,
			};
		return this.SendMessageAndWaitForResponse( "web_server_mailbox", msg, "mailbox_registered" );
	}


	//-----------------------------------------------------------------------------
	// Purpose: Sends a message and then returns a promise to wait for the expected
	//			reponse to that message.
	//-----------------------------------------------------------------------------
	public SendMessageAndWaitForResponse( sDestMailbox: string, msg: BaseMessage, sResponseMessageType: string ): Promise<BaseMessage>
	{
		// Set return address (if not already specified).
		let msgLocal = Object.assign( {}, msg ); // make a copy so we don't modify the caller's object
		if ( msgLocal.returnAddress == undefined )
		{
			msgLocal.returnAddress = this.m_sMailboxName;
		}
		msgLocal.message_id = this.m_nNextMessageNumber++;

		this.SendMessage( sDestMailbox, msgLocal );
		return this.WaitForMessage( sResponseMessageType, msgLocal.message_id );
	}

	//-----------------------------------------------------------------------------
	// Purpose: Responds to a message using the message_id and returnAddress
	//			appropriately.
	//-----------------------------------------------------------------------------
	public SendResponse( msgOriginal: BaseMessage, msgResponse: BaseMessage )
	{
		if ( !msgOriginal.returnAddress )
		{
			throw new Error( "Missing return address on message" );
		}

		let msgLocal = // make a copy so we don't modify the caller's object
			{
				...msgResponse,
				message_id: msgOriginal.message_id,
			};
		msgLocal.message_id = msgOriginal.message_id;

		this.SendMessage( msgOriginal.returnAddress, msgLocal );
	}
}

